import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Icon } from '@bciti/types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { CreateServiceCategoryPayload, UpdateServiceCategoryPayload } from 'src/app/global/services/service-state/service-state.model';
import {
    CreateServiceCategoryAction,
    CreateServiceCategoryErrorAction,
    CreateServiceCategorySuccessAction,
    DeleteServiceCategoryAction,
    DeleteServiceCategoryErrorAction,
    DeleteServiceCategorySuccessAction,
    GetServiceCategoriesAction,
    GetServiceCategoriesErrorAction,
    GetServiceCategoriesSuccessAction,
    UpdateServiceCategoryAction,
    UpdateServiceCategoryErrorAction,
    UpdateServiceCategorySuccessAction,
} from 'src/app/global/store/global.actions';
import { serviceCategoriesSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { BcitiCategoryInputComponent, CategoryInputState, CategoryOutput, IFECategory } from '../../forms/category/bciti-category-input.component';
import { GETTempICollectionCategory } from 'src/app/global/services/collections/collections.model';

export interface ICollectionCategoriesDialogTranslations {
    create: string;
    title: string;
}

@Component({
    selector: 'app-services-categories-dialog',
    templateUrl: './services-categories-dialog.component.html',
    styleUrls: ['./services-categories-dialog.component.scss'],
})
export class ServicesCategoriesDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    creating = false;
    toggleAddButton = true;
    disableActions = false;
    disabledIcons = [
        Icon.STAR_FILLED,
        Icon.HOME_FILLED,
        Icon.USER_PROFILE_FILLED,
        Icon.LINK_FILLED,
        Icon.ALERT_OCTAGON_FILLED,
        Icon.PARKING_ZONE,
        Icon.CONTENT_PEN_3,
        Icon.UNIE906,
        Icon.UNIE907,
        Icon.CHATBOT_ACTIVE,
        Icon.MAP_ACTIVE,
        Icon.UNIE930,
        Icon.UNIE931,
        Icon.ARROW_DOWN,
        Icon.ARROW_LEFT,
        Icon.ARROW_RIGHT,
        Icon.ARROW_UP,
        Icon.MENU,
        Icon.CARET_DOWN,
        Icon.CARET_LEFT,
        Icon.CARET_RIGHT,
        Icon.CARET_UP,
        Icon.VIEW_HEADLINE,
        Icon.TEXT_SUBJECT,
        Icon.AUDIO_FILE_VOLUME,
        Icon.PHONE_UPLOAD,
        Icon.PHOTO_COLLECTION,
        Icon.SD_CARD_UPLOAD,
        Icon.KEY_LOCK,
        Icon.OFFICE_FILE_PDF,
        Icon.FOLDER_NETWORK,
        Icon.REFRESH,
        Icon.USER_ADD,
        Icon.CALENDAR_ADD,
        Icon.COG,
        Icon.CHECK,
        Icon.CHECK_CIRCLE,
        Icon.CLOSE,
        Icon.CLOSE_CIRCLE,
        Icon.PLUS,
        Icon.MINUS,
        Icon.CIRCLE_PLUS,
        Icon.CIRCLE_MINUS,
        Icon.EDIT,
        Icon.FLIP,
        Icon.INFO,
        Icon.LINK,
        Icon.LIST,
        Icon.LOGOUT,
        Icon.LOGIN_2,
        Icon.MAP,
        Icon.MORE_HORIZONTAL,
        Icon.MORE_VERTICAL,
        Icon.STAR,
        Icon.DRAG_HANDLE,
        Icon.TAG,
        Icon.WARNING,
        Icon.ALERT_OCTAGON,
        Icon.ALERT,
        Icon.ALERT_CIRCLE,
        Icon.CLOCK,
        Icon.FAVORITE,
        Icon.EMAIL_ACTION_RECEIVE,
        Icon.DESKTOP_MONITOR_DOWNLOAD,
        Icon.DESKTOP_MONITOR_UPLOAD,
        Icon.INSURANCE_CARD,
        Icon.LIKE_1,
        Icon.MEGAPHONE_1,
        Icon.MESSAGES_PEOPLE_PERSON_BUBBLE_OVAL,
        Icon.MOBILE_PHONE_2,
        Icon.MODERN_TV_3D_GLASSES,
        Icon.MONEY_WALLET_OPEN,
        Icon.MOOD_HAPPY,
        Icon.MOUSE,
        Icon.OPTIMIZATION_TIMER_1,
        Icon.PHONE_ACTIONS_RING,
        Icon.PIN,
        Icon.PRESENTATION_BOARD_GRAPH,
        Icon.PRINT_TEXT,
        Icon.PROGRAMMING_FLAG,
        Icon.RAT,
        Icon.SCREEN_1,
        Icon.SEND_EMAIL_2,
        Icon.SHIPMENT_UPLOAD,
        Icon.SHOPPING_BAG_HEART,
        Icon.TAKING_PICTURES_CIRCLE,
        Icon.NO_PARKING,
        Icon.REQUESTS,
        Icon.SEARCH,
        Icon.SETTING,
        Icon.SCHEDULE_MEETING,
        Icon.TAG1,
        Icon.SLIPPERY_ICE,
        Icon.TIMER,
        Icon.TRUSTED,
        Icon.USER_PROFILE,
        Icon.VIEW_OFF,
        Icon.VIEW,
        Icon.WATER_SERVICE_INTERRUPTION,
        Icon.GRID,
        Icon.FOLDER,
        Icon.FILES,
    ];
    icons = Object.values(Icon).filter((icon) => !this.disabledIcons.includes(icon));

    loading = false;

    @ViewChildren(BcitiCategoryInputComponent) categoryInputComponents: QueryList<BcitiCategoryInputComponent>;

    categories$ = this.store.select(serviceCategoriesSelector).pipe(takeUntil(this.destroy$));

    constructor(
        private dialogRef: MatDialogRef<ServicesCategoriesDialogComponent>,
        private store: Store<State>,
        private actions$: Actions,
    ) {}

    ngOnInit(): void {
        this.actions$
            .pipe(
                ofType(GetServiceCategoriesAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetServiceCategoriesSuccessAction, GetServiceCategoriesErrorAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        // Check if we have any categories, if we do not then fetch from API.
        this.categories$
            .pipe(
                take(1),
                filter((categories) => categories.length === 0),
                tap(() => this.store.dispatch(GetServiceCategoriesAction())),
            )
            .subscribe();
    }

    close(): void {
        this.dialogRef.close();
    }

    startCreating(): void {
        this.creating = true;
        this.toggleAddButton = false;
        this.categoryInputComponents.forEach((input) => {
            if (input?.category?.name) {
                input.state = CategoryInputState.DISPLAYING;
            }
        });
    }

    handleCancelCreate(): void {
        this.creating = false;
        this.toggleAddButton = true;
    }

    toggleAddButtonDisplay($event) {
        return (this.toggleAddButton = $event === CategoryInputState.DISPLAYING);
    }

    closeAllOtherForms(targetCategory: GETTempICollectionCategory): void {
        this.creating = false;
        this.categoryInputComponents.forEach((input) => {
            // TODO: review condition when data interface is confirmed
            if (targetCategory?.name !== input?.category?.name) {
                input.state = CategoryInputState.DISPLAYING;
            }
        });
    }

    handleSave(e: CategoryOutput) {
        // all other category inputs should be disabled / cannot be opened, buttons should be disabled except to close the dialog
        const categoryInputRef = this.categoryInputComponents.find((input) => input.id === e.inputId);
        if (e.category._id) {
            // patch
            this.updateCategory(e, categoryInputRef);
        } else {
            // create
            this.createCategory(e, categoryInputRef);
        }
    }

    handleDelete(e: { category: IFECategory; inputId: string }) {
        const categoryInputRef = this.categoryInputComponents.find((input) => input.id === e.inputId);
        this.disableActions = true;
        categoryInputRef.loading = true;

        this.store.dispatch(DeleteServiceCategoryAction({ category: e.category }));

        this.actions$
            .pipe(
                ofType(DeleteServiceCategorySuccessAction),
                take(1),
                tap(() => {
                    categoryInputRef.loading = false;
                    categoryInputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(DeleteServiceCategoryErrorAction),
                take(1),
                tap((response) => {
                    if (response.error.status === 400) {
                        categoryInputRef.serviceToDeleteFirst = response.error.error.data;
                    }
                    categoryInputRef.loading = false;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    updateCategory(e: CategoryOutput, inputRef: BcitiCategoryInputComponent): void {
        this.disableActions = true;
        inputRef.loading = true;

        this.store.dispatch(UpdateServiceCategoryAction({ category: e.category as UpdateServiceCategoryPayload }));

        this.actions$
            .pipe(
                ofType(UpdateServiceCategorySuccessAction, UpdateServiceCategoryErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    inputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    createCategory(e: CategoryOutput, inputRef: BcitiCategoryInputComponent): void {
        this.disableActions = true;
        inputRef.loading = true;

        this.store.dispatch(CreateServiceCategoryAction({ category: e.category as CreateServiceCategoryPayload }));

        this.actions$
            .pipe(
                ofType(CreateServiceCategorySuccessAction, CreateServiceCategoryErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    inputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.creating = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
